/* EcoSave theme – vanilla JS, no dependencies. */
(() => {
  'use strict';

  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => [...root.querySelectorAll(sel)];
  const theme = window.theme || { routes: {}, strings: {} };
  const t = theme.strings;

  const store = {
    get(key, fallback) {
      try { return JSON.parse(localStorage.getItem(key)) ?? fallback; } catch { return fallback; }
    },
    set(key, value) {
      try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* private mode */ }
    },
  };

  function trapFocus(container) {
    container.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab') return;
      const f = $$('a[href], button:not([disabled]), input:not([type="hidden"]), select', container).filter((el) => el.offsetParent !== null);
      if (!f.length) return;
      const first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
  }

  /* ---------------------------------------------------------
     Hero video
     --------------------------------------------------------- */
  function initHero(hero) {
    const video = $('[data-hero-video]', hero);
    const toggle = $('[data-hero-toggle]', hero);
    if (!video || !toggle) return;

    const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)');
    const mobileMq = matchMedia('(max-width: 759px)');
    const conn = navigator.connection || {};
    const constrained = conn.saveData || /(^|-)2g$/.test(conn.effectiveType || '');
    let userPaused = store.get('ecosave-hero-paused', false);
    let inView = true;
    let currentSrc = '';

    const setPressed = (paused) => {
      toggle.setAttribute('aria-pressed', String(paused));
      toggle.setAttribute('aria-label', paused ? t.videoPlay : t.videoPause);
    };

    const tryPlay = () => {
      if (userPaused || !inView) return;
      const p = video.play();
      if (p && p.catch) {
        p.catch(() => {
          // Autoplay blocked (e.g. Low Power Mode): keep the still image, offer a play button.
          hero.classList.remove('is-playing');
          userPaused = true;
          setPressed(true);
        });
      }
    };

    const load = () => {
      if (reduceMotion.matches || constrained) {
        video.pause();
        hero.classList.remove('is-playing');
        toggle.hidden = true;
        return;
      }
      const mobile = mobileMq.matches;
      const src = mobile ? video.dataset.srcMobile : video.dataset.srcDesktop;
      if (!src) {
        // A still photo is set for this screen size: never request the video here.
        video.pause();
        hero.classList.remove('is-playing');
        toggle.hidden = true;
        return;
      }
      if (src && src !== currentSrc) {
        currentSrc = src;
        video.poster = mobile ? video.dataset.posterMobile : video.dataset.posterDesktop;
        video.src = src;
        video.preload = 'auto';
        video.load();
      }
      toggle.hidden = false;
      setPressed(userPaused);
      tryPlay();
    };

    video.addEventListener('playing', () => hero.classList.add('is-playing'));
    video.addEventListener('error', () => { hero.classList.remove('is-playing'); toggle.hidden = true; });

    toggle.addEventListener('click', () => {
      userPaused = !userPaused;
      store.set('ecosave-hero-paused', userPaused);
      setPressed(userPaused);
      if (userPaused) video.pause(); else tryPlay();
    });

    // Save battery when the hero is off-screen.
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(([entry]) => {
        inView = entry.isIntersecting;
        if (!inView) video.pause(); else if (currentSrc) tryPlay();
      }).observe(hero);
    }

    reduceMotion.addEventListener?.('change', load);
    mobileMq.addEventListener?.('change', load);
    load();
  }

  /* ---------------------------------------------------------
     Navigation: mobile menu and desktop dropdowns
     --------------------------------------------------------- */
  function initNav() {
    const menuBtn = $('[data-menu-toggle]');
    const menu = $('[data-mobile-menu]');
    if (menuBtn && menu) {
      const setMenu = (open) => {
        menuBtn.setAttribute('aria-expanded', String(open));
        $('.sr-only', menuBtn).textContent = open ? t.menuClose : t.menuOpen;
        menuBtn.querySelector('path').setAttribute('d', open ? 'M6 6l12 12M18 6L6 18' : 'M3 7h18M3 12h18M3 17h18');
        menu.hidden = !open;
        document.body.classList.toggle('is-locked', open);
      };
      menuBtn.addEventListener('click', () => setMenu(menuBtn.getAttribute('aria-expanded') !== 'true'));
      $$('[data-close-nav]', menu).forEach((a) => a.addEventListener('click', () => setMenu(false)));
      matchMedia('(min-width: 1120px)').addEventListener?.('change', (e) => { if (e.matches) setMenu(false); });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !menu.hidden) { setMenu(false); menuBtn.focus(); }
      });
    }

    const drops = $$('[data-nav-drop]');
    document.addEventListener('click', (e) => {
      drops.forEach((d) => { if (d.open && !d.contains(e.target)) d.open = false; });
    });
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      drops.forEach((d) => { if (d.open) { d.open = false; $('summary', d).focus(); } });
    });
  }

  /* ---------------------------------------------------------
     Search overlay with predictive search
     --------------------------------------------------------- */
  function initSearch() {
    const wrap = $('[data-search]');
    const opener = $('[data-search-open]');
    if (!wrap || !opener) return;
    const input = $('[data-search-input]', wrap);
    const results = $('[data-search-results]', wrap);
    let lastFocus = null;
    let timer = 0;
    let controller = null;

    const fetchResults = async () => {
      const q = input.value.trim();
      if (!q) { results.innerHTML = ''; return; }
      controller?.abort();
      controller = new AbortController();
      const params = new URLSearchParams({
        q,
        'resources[type]': 'product,page,article',
        'resources[limit]': '6',
        section_id: 'predictive-search',
      });
      try {
        const res = await fetch(`${theme.routes.predictiveSearch}?${params}`, { signal: controller.signal });
        if (!res.ok) throw new Error(res.status);
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const section = $('.shopify-section', doc) || doc.body;
        results.innerHTML = section.innerHTML;
      } catch (err) {
        if (err.name !== 'AbortError') results.innerHTML = '';
      }
    };

    const open = () => {
      lastFocus = document.activeElement;
      wrap.hidden = false;
      document.body.classList.add('is-locked');
      input.focus();
      if (input.value) fetchResults();
    };
    const close = () => {
      wrap.hidden = true;
      document.body.classList.remove('is-locked');
      lastFocus?.focus();
    };

    opener.addEventListener('click', (e) => { e.preventDefault(); open(); });
    wrap.addEventListener('click', (e) => { if (e.target.closest('[data-search-close]')) close(); });
    input.addEventListener('input', () => { clearTimeout(timer); timer = setTimeout(fetchResults, 200); });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !wrap.hidden) close();
      if (e.key === '/' && wrap.hidden && !/input|textarea|select/i.test(document.activeElement.tagName)) { e.preventDefault(); open(); }
    });
    trapFocus(wrap);
  }

  /* ---------------------------------------------------------
     Cart drawer (Ajax Cart API + Section Rendering API)
     --------------------------------------------------------- */
  const cart = {
    drawer: null,
    lastFocus: null,

    init() {
      this.drawer = $('[data-cart]');
      const opener = $('[data-cart-open]');
      if (!this.drawer || !opener) return;

      opener.addEventListener('click', (e) => { e.preventDefault(); this.open(); });
      this.drawer.addEventListener('click', (e) => this.onClick(e));
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !this.drawer.hidden) this.close(); });
      trapFocus(this.drawer);
      this.syncCount();
    },

    open() {
      if (!this.drawer) { location.href = theme.routes.cart; return; }
      this.lastFocus = document.activeElement;
      this.drawer.hidden = false;
      document.body.classList.add('is-locked');
      $('.drawer__head [data-cart-close]', this.drawer)?.focus();
    },

    close() {
      this.drawer.hidden = true;
      document.body.classList.remove('is-locked');
      this.lastFocus?.focus();
    },

    onClick(e) {
      if (e.target.closest('[data-cart-close]')) { this.close(); return; }
      const line = e.target.closest('[data-line]');
      if (!line) return;
      const qty = Number(line.dataset.qty);
      const step = e.target.closest('[data-qty-step]');
      if (step) this.change(line.dataset.line, Math.max(0, qty + Number(step.dataset.qtyStep)));
      else if (e.target.closest('[data-remove]')) this.change(line.dataset.line, 0);
    },

    render(sections) {
      const html = sections && sections['cart-drawer'];
      if (!html || !this.drawer) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = $('[data-cart-content]', doc);
      const current = $('[data-cart-content]', this.drawer);
      // Keep what the customer has typed in the note; the server copy may lag a pending save.
      const typed = $('[data-cart-note]', current)?.value;
      if (fresh && current) current.replaceWith(fresh);
      const field = $('[data-cart-note]', this.drawer);
      if (field && typed !== undefined) field.value = typed;
      this.syncCount();
    },

    syncCount() {
      const content = $('[data-cart-content]', this.drawer);
      const countEl = $('[data-cart-count]');
      const opener = $('[data-cart-open]');
      if (!content || !countEl) return;
      const count = Number(content.dataset.count || 0);
      countEl.textContent = count;
      countEl.hidden = count === 0;
      if (content.dataset.countLabel) opener.setAttribute('aria-label', content.dataset.countLabel);
    },

    showError(message) {
      const el = $('[data-cart-error]', this.drawer);
      if (!el) return;
      el.textContent = message;
      el.hidden = false;
    },

    async request(url, body) {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...body, sections: ['cart-drawer'], sections_url: location.pathname }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || data.message || t.addError);
      return data;
    },

    async add(items) {
      const data = await this.request(`${theme.routes.cartAdd}.js`, { items });
      this.render(data.sections);
      this.open();
    },

    async change(key, quantity) {
      this.drawer.classList.add('is-busy');
      try {
        const data = await this.request(`${theme.routes.cartChange}.js`, { id: key, quantity });
        this.render(data.sections);
      } catch (err) {
        this.showError(err.message);
      } finally {
        this.drawer.classList.remove('is-busy');
      }
    },
  };

  // Every add-to-cart form in the theme (pack cards, add-ons, collection cards, product page).
  function initAddForms() {
    document.addEventListener('submit', async (e) => {
      const form = e.target.closest('[data-add-form], [data-product-form]');
      if (!form || !cart.drawer) return;
      e.preventDefault();
      const fd = new FormData(form);
      const button = $('[type="submit"]', form);
      const error = $('[data-form-error]', form);
      if (error) error.hidden = true;
      button?.setAttribute('aria-disabled', 'true');
      try {
        await cart.add([{ id: Number(fd.get('id')), quantity: Math.max(1, Number(fd.get('quantity') || 1)) }]);
      } catch (err) {
        if (error) { error.textContent = err.message; error.hidden = false; }
        else { cart.open(); cart.showError(err.message); }
      } finally {
        button?.removeAttribute('aria-disabled');
      }
    });

    // Variant options on the homepage pack cards: swap the price shown.
    document.addEventListener('change', (e) => {
      const radio = e.target.closest('[data-variant-radio]');
      if (!radio) return;
      const price = $('p[data-price]', radio.form);
      if (price && radio.dataset.priceHtml) price.innerHTML = radio.dataset.priceHtml;
    });
  }

  /* ---------------------------------------------------------
     Order note (cart.note): saved as the customer types, kept in step between the cart drawer and
     the cart page, and saved before checkout.
     --------------------------------------------------------- */
  const cartNote = {
    saved: null,
    timer: null,
    pending: null,

    init() {
      const first = $('[data-cart-note]');
      if (first) this.saved = first.value;
      document.addEventListener('input', (e) => {
        const field = e.target.closest('[data-cart-note]');
        if (!field) return;
        $$('[data-cart-note]').forEach((other) => { if (other !== field) other.value = field.value; });
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.save(field.value), 500);
      });
      document.addEventListener('submit', async (e) => {
        const form = e.target.closest('[data-checkout-form]');
        if (!form || form.dataset.noteSaved) return;
        const field = $$('[data-cart-note]').find((el) => el.form === form);
        if (!field) return;
        e.preventDefault();
        clearTimeout(this.timer);
        try { await this.save(field.value); } catch { /* the field also posts with the form */ }
        form.dataset.noteSaved = 'true';
        form.requestSubmit(e.submitter);
      });
    },

    async save(note) {
      await this.pending;
      if (note === this.saved) return;
      this.pending = fetch(`${theme.routes.cartUpdate}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note }),
      }).then((res) => { if (res.ok) this.saved = note; });
      await this.pending;
    },
  };

  /* ---------------------------------------------------------
     Product page: variant picker, gallery, quantity
     --------------------------------------------------------- */
  function initProduct(root) {
    const form = $('[data-product-form]', root);
    const mainMedia = $('[data-main-media]', root);
    const thumbs = $$('[data-thumb]', root);

    const showImage = (src, alt, mediaId, ratio) => {
      const img = mainMedia && $('img', mainMedia);
      if (!img || !src) return;
      img.removeAttribute('srcset');
      img.src = src;
      img.alt = alt || '';
      if (ratio) mainMedia.style.setProperty('--ratio', ratio);
      thumbs.forEach((th) => th.setAttribute('aria-current', String(th.dataset.mediaId === String(mediaId))));
    };
    thumbs.forEach((th) => th.addEventListener('click', () => showImage(th.dataset.src, th.dataset.alt, th.dataset.mediaId, th.dataset.ratio)));

    if (!form) return;

    const qtyInput = $('[data-qty-input]', form);
    $$('[data-qty-step]', form).forEach((btn) => btn.addEventListener('click', () => {
      qtyInput.value = Math.max(1, Number(qtyInput.value || 1) + Number(btn.dataset.qtyStep));
    }));

    const dataEl = $('[data-variants]', form);
    if (!dataEl) return;
    const variants = JSON.parse(dataEl.textContent);
    const idInput = $('[data-variant-id]', form);
    const button = $('[data-add-button]', form);
    const price = $('[data-price]', root);
    const stock = $('[data-stock]', form);

    form.addEventListener('change', (e) => {
      if (!e.target.matches('[data-option]')) return;
      const selected = $$('fieldset', form).map((fs) => $('input:checked', fs)?.value);
      const variant = variants.find((v) => v.options.every((o, i) => o === selected[i]));

      if (!variant) {
        button.disabled = true;
        button.textContent = t.unavailable;
        if (stock) { stock.textContent = t.unavailable; stock.classList.add('stock--out'); }
        return;
      }
      if (stock) {
        stock.textContent = variant.available ? t.inStock : t.soldOut;
        stock.classList.toggle('stock--out', !variant.available);
      }
      idInput.value = variant.id;
      button.disabled = !variant.available;
      button.textContent = variant.available ? t.addToCart : t.soldOut;
      if (price) price.innerHTML = variant.price;
      if (variant.media) showImage(variant.media.src, variant.media.alt, variant.media.id, variant.media.ratio);

      const url = new URL(location.href);
      url.searchParams.set('variant', variant.id);
      history.replaceState({}, '', url);
    });
  }

  /* ---------------------------------------------------------
     Product page: complementary products (Search & Discovery)
     --------------------------------------------------------- */
  async function initRecommendations(el) {
    const slot = $('[data-recommendations-slot]', el);
    if (!slot || !el.dataset.url) return;
    try {
      const res = await fetch(el.dataset.url);
      if (!res.ok) return;
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const list = $('[data-recommendations-result]', doc);
      if (!list) return;
      slot.replaceChildren(list);
      el.hidden = false;
    } catch { /* leave the block hidden */ }
  }

  /* ---------------------------------------------------------
     Product page: related products (Shopify recommendations)
     --------------------------------------------------------- */
  async function initRelated(el) {
    const list = $('[data-related-list]', el);
    if (!list || !el.dataset.url) return;
    try {
      const res = await fetch(el.dataset.url);
      if (!res.ok) return;
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const result = $('[data-related-result]', doc);
      if (result) list.replaceWith(result);
    } catch { /* keep the collection products */ }
  }

  /* ---------------------------------------------------------
     Product description: the descriptions are written in the admin editor with ✓ lines separated by
     <br>, manual line breaks and bold/underline everywhere. Rebuild them into a heading, a tick list
     and plain paragraphs. The words and links are kept; only the structure changes.
     --------------------------------------------------------- */
  function tidyDescription(desc) {
    const TICK = /^\s*[✓✔]\s*/;
    const textOf = (nodes) => nodes.map((n) => n.textContent).join('').replace(/\s+/g, ' ').trim();
    const isBold = (n) => {
      if (!n.textContent.trim()) return true;
      if (n.nodeType !== 1) return false;
      if (n.matches('strong, b')) return true;
      return [...n.querySelectorAll('strong, b')].map((x) => x.textContent).join('').trim() === n.textContent.trim();
    };
    const fragmentOf = (nodes, stripTick) => {
      const frag = document.createDocumentFragment();
      nodes.forEach((n) => frag.append(n.cloneNode(true)));
      if (stripTick) {
        const walker = document.createTreeWalker(frag, NodeFilter.SHOW_TEXT);
        for (let t = walker.nextNode(); t; t = walker.nextNode()) {
          if (!t.textContent.trim()) continue;
          t.textContent = t.textContent.replace(TICK, '');
          if (t.textContent.trim()) break;
        }
      }
      return frag;
    };
    // Every block is split into lines at <br>; inline wrappers around a <br> are split with it.
    // An empty line, or the end of a block, ends a paragraph.
    const lines = [];
    const pushLines = (block) => {
      let current = [];
      const walk = (node, wrap) => {
        node.childNodes.forEach((child) => {
          if (child.nodeName === 'BR') { lines.push(current); current = []; return; }
          if (child.nodeType === 1 && child.querySelector('br')) {
            walk(child, (n) => { const w = child.cloneNode(false); w.append(n); return wrap(w); });
            return;
          }
          current.push(wrap(child.cloneNode(true)));
        });
      };
      walk(block, (n) => n);
      lines.push(current, null);
    };
    // "Question? Answer…" on one line, with only the question in bold: the question becomes its own line.
    const splitQuestion = (nodes) => {
      const lead = [];
      let i = 0;
      for (; i < nodes.length && isBold(nodes[i]); i++) lead.push(nodes[i]);
      const rest = nodes.slice(i);
      if (lead.length && rest.length && /\?\s*$/.test(textOf(lead)) && textOf(rest)) return [lead, rest];
      return [nodes];
    };
    [...desc.children].forEach((block) => { if (block.tagName === 'P' || block.tagName === 'DIV') pushLines(block); });
    for (let i = lines.length - 1; i >= 0; i--) if (lines[i]) lines.splice(i, 1, ...splitQuestion(lines[i]));

    const result = [];
    let list = null;
    let para = null;
    let seenList = false;
    const isTickLine = (l) => l && TICK.test(textOf(l));
    const nextLine = (i) => { for (let j = i + 1; j < lines.length; j++) if (lines[j] && textOf(lines[j])) return lines[j]; return null; };
    let lineIndex = 0;
    [...desc.children].forEach((block) => {
      if (block.tagName !== 'P' && block.tagName !== 'DIV') {
        list = null; para = null;
        const el = block.cloneNode(true);
        if (/^H[1-6]$/.test(el.tagName)) { const h = document.createElement('h2'); h.className = 'desc__heading'; h.textContent = el.textContent.trim(); result.push(h); }
        else result.push(el);
        return;
      }
      for (; lineIndex < lines.length; lineIndex++) {
        const nodes = lines[lineIndex];
        if (nodes === null) { lineIndex++; break; }
        const text = textOf(nodes);
        if (!text) { para = null; continue; }
        if (TICK.test(text)) {
          para = null;
          if (!list) { list = document.createElement('ul'); list.className = 'desc__ticks'; result.push(list); seenList = true; }
          const li = document.createElement('li');
          const line = document.createElement('span');
          line.append(fragmentOf(nodes, true));
          li.append(line);
          list.append(li);
          continue;
        }
        list = null;
        const bold = nodes.every(isBold);
        const introducesList = isTickLine(nextLine(lineIndex));
        if (text.length < 80 && introducesList && (/:$/.test(text) || (bold && !/[.!]$/.test(text)))) {
          para = null;
          const h = document.createElement('h2'); h.className = 'desc__heading'; h.textContent = text; result.push(h);
          continue;
        }
        if (text.length < 80 && /\?$/.test(text)) {
          para = null;
          const q = document.createElement('p'); q.className = 'desc__sub'; q.textContent = text; result.push(q);
          continue;
        }
        if (!para) {
          para = document.createElement('p');
          if (seenList && bold) para.className = 'desc__note';
          result.push(para);
        } else {
          para.append(' ');
        }
        para.append(fragmentOf(nodes, false));
      }
      para = null;
    });
    // Inline styles from the admin editor (mostly underlines) and the runs of spaces used for indenting are
    // dropped; the words and links stay.
    result.forEach((n) => {
      n.querySelectorAll?.('[style]').forEach((el) => el.removeAttribute('style'));
      const walker = document.createTreeWalker(n, NodeFilter.SHOW_TEXT);
      const texts = [];
      for (let t = walker.nextNode(); t; t = walker.nextNode()) { t.textContent = t.textContent.replace(/[\s\u00a0]+/g, ' '); texts.push(t); }
      const first = texts.find((t) => t.textContent.trim());
      const last = [...texts].reverse().find((t) => t.textContent.trim());
      texts.forEach((t) => { if (t === first) return; if (!first || texts.indexOf(t) < texts.indexOf(first)) t.textContent = ''; });
      if (first) first.textContent = first.textContent.replace(/^ +/, '');
      if (last) last.textContent = last.textContent.replace(/ +$/, '');
      texts.forEach((t) => { if (last && texts.indexOf(t) > texts.indexOf(last)) t.textContent = ''; });
    });
    desc.replaceChildren(...result);
    desc.classList.add('is-tidy');
  }

  /* ---------------------------------------------------------
     Video section: load YouTube/Vimeo only after a click
     --------------------------------------------------------- */
  function initVideoEmbeds(root = document) {
    $$('[data-video-embed]', root).forEach((frame) => {
      const play = $('[data-video-play]', frame);
      play?.addEventListener('click', () => {
        const iframe = document.createElement('iframe');
        iframe.src = frame.dataset.src;
        iframe.title = frame.dataset.title || '';
        iframe.allow = 'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture; fullscreen';
        iframe.allowFullscreen = true;
        frame.replaceChildren(iframe);
        frame.classList.add('is-playing');
      }, { once: true });
    });
  }

  /* ---------------------------------------------------------
     Product reviews: Nordtrust data rendered as text cards
     --------------------------------------------------------- */
  async function initReviews(root) {
    const track = $('[data-reviews-track]', root);
    const stringsEl = $('[data-reviews-strings]', root);
    if (!track || !stringsEl || root.dataset.loaded) return;
    root.dataset.loaded = 'true';
    const s = JSON.parse(stringsEl.textContent);
    const endpoint = root.dataset.endpoint;
    const productId = root.dataset.productId;
    const max = Number(root.dataset.max) || 12;
    const minCards = Number(root.dataset.minCards) || 3;
    const get = async (query) => {
      const res = await fetch(endpoint + query, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error(res.status);
      return res.json();
    };

    let product = { summary: { total: 0 }, reviews: [] };
    let store = null;
    try {
      if (productId) product = await get(`?product_id=${encodeURIComponent(productId)}`);
      const own = product.reviews || [];
      if (root.dataset.fill === 'true' && own.length < minCards) store = await get('');
    } catch {
      return; // Leave the section hidden if the review service is unavailable.
    }

    const seen = new Set();
    const reviews = [...(product.reviews || []), ...((store && store.reviews) || [])]
      .filter((r) => r && r.body && !seen.has(r.id) && seen.add(r.id))
      .slice(0, max);
    if (!reviews.length) return;

    // Summary: the product's own numbers when it has reviews, otherwise the store's.
    const useStore = !(product.summary && product.summary.total) && store;
    const summary = useStore ? store.summary : product.summary;
    const avg = Number(summary.average || 0).toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    const count = summary.total;
    const basedOn = useStore ? s.basedOnStore : (count === 1 ? s.basedOnProductOne : s.basedOnProduct);
    const summaryEl = $('[data-reviews-summary]', root);
    summaryEl.replaceChildren();
    const stars = (rating) => {
      const el = document.createElement('span');
      el.className = 'review-stars';
      el.setAttribute('role', 'img');
      el.setAttribute('aria-label', s.stars.replace('%r', rating));
      el.textContent = '★★★★★'.slice(0, Math.round(rating)) + '☆☆☆☆☆'.slice(0, 5 - Math.round(rating));
      return el;
    };
    const score = document.createElement('strong');
    score.textContent = `${avg} ${s.outOf}`;
    const based = document.createElement('span');
    based.textContent = basedOn.replace('%c', count);
    summaryEl.append(stars(Number(summary.average || 0)), score, based);

    const frag = document.createDocumentFragment();
    reviews.forEach((r) => {
      const li = document.createElement('li');
      li.className = 'review-card';
      const top = document.createElement('div');
      top.className = 'review-card__top';
      top.append(stars(r.rating));
      li.append(top);
      if (r.title) {
        const h = document.createElement('h3');
        h.className = 'review-card__title';
        h.textContent = r.title;
        li.append(h);
      }
      const body = document.createElement('p');
      body.className = 'review-card__body';
      body.textContent = r.body;
      li.append(body);
      const foot = document.createElement('p');
      foot.className = 'review-card__who';
      const name = document.createElement('span');
      name.className = 'review-card__name';
      name.textContent = r.customerName;
      foot.append(name);
      if (r.verified) {
        const v = document.createElement('span');
        v.className = 'review-card__verified';
        v.textContent = s.verified;
        foot.append(v);
      }
      li.append(foot);
      if (String(r.productId) !== String(productId) && r.productTitle) {
        const about = document.createElement('p');
        about.className = 'review-card__product';
        about.textContent = s.about.replace('%p', r.productTitle);
        li.append(about);
      }
      frag.append(li);
    });
    track.replaceChildren(frag);
    root.hidden = false;
    $('[data-reviews-hint]', root)?.remove();

    // Long reviews are shortened to a few lines so one long text does not stretch every card.
    $$('.review-card__body', track).forEach((body) => {
      if (body.scrollHeight <= body.clientHeight + 2) return;
      const more = document.createElement('button');
      more.type = 'button';
      more.className = 'review-card__more';
      more.textContent = s.readMore;
      more.setAttribute('aria-expanded', 'false');
      more.addEventListener('click', () => {
        const open = body.classList.toggle('is-open');
        more.textContent = open ? s.readLess : s.readMore;
        more.setAttribute('aria-expanded', String(open));
      });
      body.after(more);
    });

    // Arrows scroll by one card; hidden when everything fits.
    const nav = $('[data-reviews-nav]', root);
    const prev = $('[data-reviews-prev]', root);
    const next = $('[data-reviews-next]', root);
    const step = () => {
      const card = $('.review-card', track);
      return card ? card.getBoundingClientRect().width + parseFloat(getComputedStyle(track).columnGap || 0) : track.clientWidth;
    };
    const update = () => {
      const overflow = track.scrollWidth > track.clientWidth + 2;
      nav.hidden = !overflow;
      prev.disabled = track.scrollLeft <= 2;
      next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
    };
    prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
    next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
    track.addEventListener('scroll', update, { passive: true });
    addEventListener('resize', update);
    update();
  }

  /* ---------------------------------------------------------
     Manuals: save PDFs under a readable name. The download attribute
     is ignored for files on another domain (Shopify's CDN), so fetch
     the file and save it as a blob; open it in a new tab if that fails.
     --------------------------------------------------------- */
  document.addEventListener('click', async (e) => {
    const link = e.target.closest('[data-download]');
    if (!link || !link.dataset.download) return;
    e.preventDefault();
    try {
      const res = await fetch(link.href);
      if (!res.ok) throw new Error(res.status);
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement('a');
      a.href = url;
      a.download = link.dataset.download;
      document.body.append(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      window.open(link.href, '_blank', 'noopener');
    }
  });

  $$('[data-hero]').forEach(initHero);
  initNav();
  initSearch();
  cart.init();
  cartNote.init();
  initAddForms();
  $$('[data-product]').forEach(initProduct);
  $$('[data-recommendations]').forEach(initRecommendations);
  $$('[data-related]').forEach(initRelated);
  $$('[data-product-desc]').forEach(tidyDescription);
  $$('[data-reviews]').forEach(initReviews);
  initVideoEmbeds();

  // Re-run section scripts when a section is changed in the theme editor.
  document.addEventListener('shopify:section:load', (e) => {
    $$('[data-hero]', e.target).forEach(initHero);
    $$('[data-product]', e.target).forEach(initProduct);
    $$('[data-recommendations]', e.target).forEach(initRecommendations);
    $$('[data-related]', e.target).forEach(initRelated);
    $$('[data-product-desc]', e.target).forEach(tidyDescription);
    $$('[data-reviews]', e.target).forEach(initReviews);
    initVideoEmbeds(e.target);
  });
})();
